import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Users,
  UserPlus,
  Mail,
  Shield,
  Loader2,
  MoreVertical,
  UserMinus,
  Ban,
  Clock,
  CheckCircle,
  Timer,
} from 'lucide-react';
import { ExtraTimeDialog } from '@/components/ExtraTimeDialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const TeamManagement = () => {
  const { api, user: currentUser } = useAuth();
  const [members, setMembers] = useState([]);
  const [extraTimeUser, setExtraTimeUser] = useState(null);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', team_role: 'staff' });
  const [orgInfo, setOrgInfo] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [membersRes, invitationsRes, orgRes] = await Promise.all([
        api.get('/organisations/team/members'),
        api.get('/organisations/team/invitations'),
        api.get('/organisations/my'),
      ]);
      setMembers(membersRes.data);
      setInvitations(invitationsRes.data);
      setOrgInfo(orgRes.data);
    } catch (error) {
      console.error('Failed to fetch team data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async () => {
    if (!inviteForm.email.trim()) {
      toast.error('Email is required');
      return;
    }
    setInviting(true);
    try {
      await api.post('/organisations/team/invite', inviteForm);
      toast.success(`Invitation sent to ${inviteForm.email}`);
      setInviteForm({ email: '', team_role: 'staff' });
      setInviteOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invitation');
    } finally {
      setInviting(false);
    }
  };

  const handleUpdateRole = async (userId, newRole) => {
    try {
      await api.put(`/organisations/team/${userId}/role?team_role=${newRole}`);
      toast.success('Role updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update role');
    }
  };

  const handleSuspend = async (userId) => {
    try {
      const res = await api.put(`/organisations/team/${userId}/suspend`);
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update member');
    }
  };

  const handleRemove = async (userId) => {
    if (!window.confirm('Remove this team member? They will lose access to your organisation.')) return;
    try {
      await api.delete(`/organisations/team/${userId}`);
      toast.success('Team member removed');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove member');
    }
  };

  const handleCancelInvite = async (invitationId) => {
    try {
      await api.delete(`/organisations/team/invitations/${invitationId}`);
      toast.success('Invitation cancelled');
      fetchData();
    } catch (error) {
      toast.error('Failed to cancel invitation');
    }
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case 'admin': return 'bg-[#f8fafc] text-[#02d1d4] border-[#02d1d4]/20';
      case 'manager': return 'bg-[#f8fafc] text-[#20acf6] border-[#20acf6]/20';
      default: return 'bg-gray-100 text-gray-600 border-gray-200';
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active': return 'bg-[#f8fafc] text-[#2a3e5d]';
      case 'suspended': return 'bg-[#F4A261]/15 text-[#F4A261]';
      default: return 'bg-gray-100 text-gray-500';
    }
  };

  if (loading) {
    return (
      <DashboardLayout requiredRoles={['org_admin']}>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-[#20acf6]" />
        </div>
      </DashboardLayout>
    );
  }

  const pendingInvitations = invitations.filter(i => i.status === 'pending');

  return (
    <DashboardLayout requiredRoles={['org_admin']}>
      <div className="space-y-6" data-testid="team-management">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#2a3e5d]">Team Management</h1>
            <p className="text-sm text-[#1e293b]/60 mt-1">
              {orgInfo?.name} &middot; {members.length}/{orgInfo?.max_team_members || 50} members
            </p>
          </div>
          <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#20acf6] hover:bg-[#3bb8f7] text-white" data-testid="invite-btn">
                <UserPlus className="h-4 w-4 mr-2" />
                Invite Member
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Invite Team Member</DialogTitle>
                <DialogDescription>
                  Send an email invitation to join your organisation.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Email address</Label>
                  <Input
                    type="email"
                    placeholder="colleague@example.com"
                    value={inviteForm.email}
                    onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                    className="border-[#f8fafc] focus:border-[#20acf6]"
                    data-testid="invite-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Role</Label>
                  <Select value={inviteForm.team_role} onValueChange={(v) => setInviteForm({ ...inviteForm, team_role: v })}>
                    <SelectTrigger className="border-[#f8fafc]" data-testid="invite-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="manager">Manager</SelectItem>
                      <SelectItem value="staff">Staff</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-[#1e293b]/50">
                    Admin: Full org access. Manager: View reports. Staff: Learning only.
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setInviteOpen(false)}>Cancel</Button>
                <Button
                  onClick={handleInvite}
                  disabled={inviting}
                  className="bg-[#20acf6] hover:bg-[#3bb8f7] text-white"
                  data-testid="send-invite-btn"
                >
                  {inviting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Mail className="h-4 w-4 mr-2" />}
                  Send Invitation
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Pending Invitations */}
        {pendingInvitations.length > 0 && (
          <Card className="border-[#F4A261]/30 bg-[#F4A261]/5">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2 text-[#1e293b]">
                <Clock className="h-4 w-4 text-[#F4A261]" />
                Pending Invitations ({pendingInvitations.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="space-y-2">
                {pendingInvitations.map((inv) => (
                  <div key={inv.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-[#f8fafc]" data-testid={`invitation-${inv.id}`}>
                    <div className="flex items-center gap-3">
                      <Mail className="h-4 w-4 text-[#F4A261]" />
                      <div>
                        <span className="text-sm font-medium text-[#1e293b]">{inv.email}</span>
                        <Badge className={`ml-2 text-xs ${getRoleBadge(inv.team_role)}`}>{inv.team_role}</Badge>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleCancelInvite(inv.id)}
                      className="text-[#F4A261] hover:bg-[#F4A261]/10"
                    >
                      Cancel
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Team Members */}
        <Card className="border-[#f8fafc]">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2 text-[#1e293b]">
              <Users className="h-4 w-4 text-[#20acf6]" />
              Team Members ({members.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            {members.length === 0 ? (
              <p className="text-center text-[#1e293b]/50 py-8">No team members yet. Send an invitation to get started.</p>
            ) : (
              <div className="space-y-2">
                {members.map((member) => {
                  const isCurrentUser = member.id === currentUser?.id;
                  return (
                    <div
                      key={member.id}
                      className="flex items-center justify-between p-3 rounded-lg border border-[#f8fafc] hover:border-[#20acf6]/30 transition-colors"
                      data-testid={`member-${member.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-[#20acf6] rounded-full flex items-center justify-center">
                          <span className="text-white text-sm font-medium">
                            {member.first_name?.[0]}{member.last_name?.[0]}
                          </span>
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-[#1e293b]">
                              {member.first_name} {member.last_name}
                              {isCurrentUser && <span className="text-[#1e293b]/40 ml-1">(you)</span>}
                            </span>
                            <Badge className={`text-xs ${getRoleBadge(member.team_role || 'staff')}`}>
                              {member.team_role || 'staff'}
                            </Badge>
                            <Badge className={`text-xs ${getStatusBadge(member.status)}`}>
                              {member.status}
                            </Badge>
                          </div>
                          <span className="text-xs text-[#1e293b]/50">{member.email}</span>
                        </div>
                      </div>
                      {!isCurrentUser && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`member-actions-${member.id}`}>
                              <MoreVertical className="h-4 w-4 text-[#1e293b]/50" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleUpdateRole(member.id, 'admin')}>
                              <Shield className="mr-2 h-4 w-4" /> Set as Admin
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleUpdateRole(member.id, 'manager')}>
                              <Shield className="mr-2 h-4 w-4" /> Set as Manager
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleUpdateRole(member.id, 'staff')}>
                              <Shield className="mr-2 h-4 w-4" /> Set as Staff
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleSuspend(member.id)}>
                              <Ban className="mr-2 h-4 w-4" />
                              {member.status === 'suspended' ? 'Activate' : 'Suspend'}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setExtraTimeUser(member)} data-testid={`member-extra-time-${member.id}`}>
                              <Timer className="mr-2 h-4 w-4" /> Extra time…
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleRemove(member.id)} className="text-red-500">
                              <UserMinus className="mr-2 h-4 w-4" /> Remove
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      <ExtraTimeDialog user={extraTimeUser} open={!!extraTimeUser} onOpenChange={(o) => !o && setExtraTimeUser(null)} />
    </DashboardLayout>
  );
};

export default TeamManagement;
